import React, { useState } from 'react'
import { CheckCircle, Lock, ArrowRight } from 'lucide-react'
import Seo from '../components/Seo'

const StrongAgainEnrollment: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [billing, setBilling] = useState<'monthly' | 'paidInFull'>('monthly')

  const handleCheckout = async () => {
    setLoading(true)
    setError('')

    try {
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          packageKey: billing === 'monthly' ? 'strongAgain' : 'strongAgain-paidInFull',
        }),
      })

      const data = await response.json()

      if (!response.ok || !data.url) {
        throw new Error(data.error || 'Unable to start checkout.')
      }

      window.location.href = data.url
    } catch (err) {
      console.error('Checkout error:', err)
      setError(
        'We were unable to start secure checkout. Please try again or contact Cassie for assistance.'
      )
      setLoading(false)
    }
  }

  return (
    <main className="min-h-screen bg-brand-cream text-brand-navy">
      <Seo
        title="Enroll in The Strong Again Method™ — Fit by Cassie"
        description="Complete your enrollment in The Strong Again Method™, private 1:1 coaching with Cassie."
      />

      <section className="px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
        <div className="container-max">
          <div className="max-w-3xl mx-auto">

            <div className="text-center mb-10">
              <span className="inline-block text-xs font-bold uppercase tracking-widest-xl text-brand-teal mb-4">
                Private Client Enrollment
              </span>

              <h1 className="font-display text-4xl sm:text-5xl font-semibold text-brand-navy leading-tight mb-5">
                The Strong Again{' '}
                <em className="italic font-normal text-brand-teal">
                  Method™
                </em>
              </h1>

              <p className="text-lg text-brand-navy/65 max-w-2xl mx-auto leading-relaxed">
                You're ready for the next step. Review your founding client
                enrollment details below, then continue to secure checkout.
              </p>
            </div>

            <div className="bg-brand-ivory rounded-3xl border border-brand-bronze/25 shadow-soft-lg p-7 sm:p-10">

              <span className="inline-block rounded-full bg-brand-blush/20 px-4 py-2 text-xs font-bold uppercase tracking-widest text-brand-navy mb-6">
                Founding Client Enrollment
              </span>

              <h2 className="font-display text-3xl font-semibold text-brand-navy mb-2">
                Private 1:1 Coaching
              </h2>

              <p className="text-brand-navy/60 mb-8">
                Personalized strength, nutrition, recovery, and accountability
                coaching with Cassie.
              </p>

              <div className="rounded-2xl bg-brand-cream border border-brand-teal/15 p-6 sm:p-7 mb-8">
                <p className="text-xs font-bold uppercase tracking-widest text-brand-teal mb-2">
                  Founding Client — 3-Month Commitment
                </p>

                <div className="grid grid-cols-2 gap-3 mb-5">
                  <button
                    type="button"
                    onClick={() => setBilling('monthly')}
                    className={`rounded-xl border px-4 py-3 text-left transition-colors ${
                      billing === 'monthly'
                        ? 'border-brand-teal bg-brand-ivory'
                        : 'border-brand-bronze/20 bg-transparent'
                    }`}
                  >
                    <span className="block text-xs font-bold uppercase tracking-widest text-brand-teal mb-1">
                      Pay Monthly
                    </span>
                    <span className="block font-display text-xl font-semibold text-brand-navy">
                      $347/mo
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setBilling('paidInFull')}
                    className={`rounded-xl border px-4 py-3 text-left transition-colors ${
                      billing === 'paidInFull'
                        ? 'border-brand-teal bg-brand-ivory'
                        : 'border-brand-bronze/20 bg-transparent'
                    }`}
                  >
                    <span className="block text-xs font-bold uppercase tracking-widest text-brand-teal mb-1">
                      Paid in Full
                    </span>
                    <span className="block font-display text-xl font-semibold text-brand-navy">
                      $1,041
                    </span>
                  </button>
                </div>

                <p className="font-display text-4xl sm:text-5xl font-semibold text-brand-navy">
                  {billing === 'monthly' ? '$347/month' : '$1,041'}
                </p>

                <p className="text-sm text-brand-navy/60 mt-2">
                  {billing === 'monthly'
                    ? '$1,041 minimum investment over your 3-month commitment'
                    : 'One-time payment covering your full 3-month commitment'}
                </p>
              </div>

              <div className="space-y-4 mb-8">
                {[
                  'Fully customized strength-training program',
                  'Personalized nutrition coaching',
                  'Cardio programming when appropriate',
                  'Supplement recommendations when appropriate',
                  'Regular coaching consultations',
                  'Progress reviews and program adjustments',
                  'Recovery and stress-management guidance',
                  'Direct coaching support from Cassie',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle
                      size={18}
                      className="text-brand-teal shrink-0 mt-0.5"
                    />
                    <span className="text-brand-navy/70">
                      {item}
                    </span>
                  </div>
                ))}
              </div>

              <div className="border-t border-brand-bronze/20 pt-7 mb-7">
                <h3 className="font-display text-xl font-semibold text-brand-navy mb-3">
                  Your commitment
                </h3>

                <p className="text-sm text-brand-navy/65 leading-relaxed">
                  {billing === 'monthly' ? (
                    <>
                      By continuing to checkout, you are enrolling in The Strong
                      Again Method™ at $347 per month with a minimum three-month
                      commitment. Your subscription will be billed monthly according
                      to the terms presented during checkout and the Fit by Cassie
                      Terms of Service.
                    </>
                  ) : (
                    <>
                      By continuing to checkout, you are enrolling in The Strong
                      Again Method™ for a one-time payment of $1,041, covering your
                      full three-month commitment in advance, according to the
                      terms presented during checkout and the Fit by Cassie
                      Terms of Service.
                    </>
                  )}
                </p>
              </div>

              {error && (
                <div className="rounded-2xl bg-red-50 border border-red-200 p-4 mb-6">
                  <p className="text-sm text-red-700">
                    {error}
                  </p>
                </div>
              )}

              <button
                type="button"
                onClick={handleCheckout}
                disabled={loading}
                className="inline-flex w-full items-center justify-center rounded-full bg-brand-navy text-brand-cream font-bold px-7 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-soft-lg disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              >
                {loading ? (
                  'Opening Secure Checkout...'
                ) : (
                  <>
                    Enroll & Continue to Secure Checkout
                    <ArrowRight className="ml-2" size={18} />
                  </>
                )}
              </button>

              <div className="flex items-center justify-center gap-2 mt-4 text-xs text-brand-navy/50">
                <Lock size={13} />
                Secure checkout powered by Stripe
              </div>

              <p className="text-center text-xs text-brand-navy/45 mt-3">
                Your payment information is entered securely during checkout.
              </p>

            </div>
          </div>
        </div>
      </section>
    </main>
  )
}

export default StrongAgainEnrollment
